import { signOut as _signOut, refreshHandleClaim, signInWithCustomToken } from '$lib/firebase/auth/auth.svelte';
import { isFeatureEnabled } from '@core/product';
import { FeatureFlag } from '@core/product/flags';
import type { User } from 'firebase/auth';
import { CookieId, expireCookie } from './cookies';

const debugLog = () => isFeatureEnabled(FeatureFlag.DebugAuth);

/**
 * Mints the server session cookie from the client's Firebase ID token and
 * returns the canonical handle/name resolved server-side. The handle is
 * present when an existing Profile aggregate was found for this user; the
 * caller can use it to route directly to `/@handle/...` instead of bouncing
 * through `/onboarding` when the client's `displayName` claim has drifted.
 */
export async function signInServer(user: User): Promise<{ handle?: string; name?: string }> {
  const idToken = await user.getIdToken();
  const response = await fetch('/api/sign-in', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  if (debugLog()) console.debug('api.client|signInServer|response', response);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message ?? `Server sign-in failed (${response.status})`);
  }
  const body = await response.json().catch(() => ({}));
  return { handle: body.handle, name: body.name };
}

export async function signOut() {
  if (debugLog()) console.debug('api.client|signOut|signing out...');
  await fetch('/api/sign-out', { method: 'DELETE' });
  await _signOut();
  expireCookie(CookieId.NeedsSessionRefresh);
  if (debugLog()) console.debug('api.client|signOut|done');
}

/**
 * Aborts an unverified registration — deletes the Firebase Auth user, clears
 * the session, and signs the client out. For users who entered the wrong
 * email and would otherwise be stuck on /verify-email forever.
 *
 * Refused server-side for verified users.
 */
export async function cancelRegistration() {
  if (debugLog()) console.debug('api.client|cancelRegistration|starting');
  const response = await fetch('/api/auth/cancel-registration', { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message ?? `Cancel registration failed (${response.status})`);
  }
  await _signOut();
  expireCookie(CookieId.NeedsSessionRefresh);
  if (debugLog()) console.debug('api.client|cancelRegistration|done');
}

export async function refreshSession(user: User): Promise<boolean> {
  if (!user) {
    if (debugLog()) console.debug('api.client|refreshSession|no user, skipping.');
    return false;
  }

  if (debugLog()) console.debug('api.client|refreshSession|user', user);
  if (!user.emailVerified) {
    if (debugLog()) console.debug('api.client|refreshSession|email not verified, skipping.');
    return false;
  }
  const idToken = await user.getIdToken(true);

  if (debugLog()) console.debug('api.client|refreshSession|fetching...');
  const response = await fetch('/api/refresh-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  if (debugLog()) console.debug('api.client|refreshSession|response', response);

  if (!response.ok) {
    console.error('api.client|refreshSession|server error', response.status);
    return false;
  }

  expireCookie(CookieId.NeedsSessionRefresh);
  // The forced getIdToken(true) above already refreshed the client's token,
  // so the handle custom claim is now current. Pull it into the module
  // cache so reactive AuthState reflects it without each consumer needing
  // to call refreshHandleClaim themselves.
  await refreshHandleClaim(user);
  return true;
}

export async function restoreSession() {
  if (debugLog()) console.debug('api.client|restoreSession|no client user, restoring from server session...');

  // Matches api\restore-session\+server.ts
  interface RestoreSessionResult {
    signedIn: boolean;
    customToken?: string;
  }

  const response = await fetch('/api/restore-session', { credentials: 'include' });
  if (!response.ok) {
    console.error('api.client|restoreSession|server error, signing out', response.status);
    await signOut();
    return;
  }

  const restore = (await response.json()) as RestoreSessionResult;
  if (debugLog()) console.debug('api.client|restoreSession', { response, restore });
  if (restore.customToken) {
    const signInResult = await signInWithCustomToken(restore.customToken);
    if (debugLog()) console.debug('api.client|restoreSession|signInWithCustomToken', signInResult);
    if (signInResult.error) {
      console.error('Error restoring session:', signInResult.error);
      await signOut();
    } else {
      expireCookie(CookieId.NeedsSessionRefresh);
    }
  } else {
    if (debugLog()) console.debug('api.client|restoreSession|no custom token, signing out');
    await signOut();
  }

  if (debugLog()) console.debug('api.client|restoreSession|done');
}
